import React, { useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import {
  useEquipmentTypeByFilterAll,
  useCreateEquipmentTypeMutation,
  useUpdateEquipmentTypeMutation,
  useDeleteEquipmentTypeMutation,
} from "presentation/hooks/use-query-equipment-type";
import {
  useEquipmentAttributeDefinitions,
  useCreateEquipmentAttributeDefinitionMutation,
  useUpdateEquipmentAttributeDefinitionMutation,
} from "presentation/hooks/use-query-equipments";
import { EquipmentAttributeDefinition } from "domain/entities/equipment-attribute-definition";
import Spinner from "presentation/components/Shared/Spinner";
import InputTextComponent from "presentation/components/Shared/InputTextComponent";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

const EquipmentTemplatesTab: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [selectedEqTypeId, setSelectedEqTypeId] = useState<string>("");
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [attrDialogMode, setAttrDialogMode] = useState<"CREATE" | "EDIT">(
    "CREATE",
  );
  const [newAttr, setNewAttr] = useState<EquipmentAttributeDefinition>({
    equipmentTypeId: "",
    name: "",
    type: "TEXT",
    isRequired: false,
    isUserEditable: false,
    options: "",
  } as EquipmentAttributeDefinition);

  const [isTypeDialogVisible, setIsTypeDialogVisible] = useState(false);
  const [typeDialogMode, setTypeDialogMode] = useState<"CREATE" | "EDIT">(
    "CREATE",
  );
  const [typeName, setTypeName] = useState("");
  const [minDeliveryPhotos, setMinDeliveryPhotos] = useState(0);
  const [minReturnPhotos, setMinReturnPhotos] = useState(0);
  const [photoInstructions, setPhotoInstructions] = useState("");

  const createTypeMutation = useCreateEquipmentTypeMutation();
  const updateTypeMutation = useUpdateEquipmentTypeMutation();
  const deleteTypeMutation = useDeleteEquipmentTypeMutation();

  const { data: eqTypesResponse, isLoading: loadingTypes } =
    useEquipmentTypeByFilterAll({ limit: 100 } as any);

  const { data: attrResponse, isLoading: loadingAttrs } =
    useEquipmentAttributeDefinitions(
      { equipmentTypeId: selectedEqTypeId },
      !!selectedEqTypeId,
    );

  const createAttrMutation = useCreateEquipmentAttributeDefinitionMutation();
  const updateAttrMutation = useUpdateEquipmentAttributeDefinitionMutation();

  const handleOpenCreateType = () => {
    setTypeDialogMode("CREATE");
    setTypeName("");
    setMinDeliveryPhotos(0);
    setMinReturnPhotos(0);
    setPhotoInstructions("");
    setIsTypeDialogVisible(true);
  };

  const handleOpenEditType = () => {
    if (!selectedEqTypeId) return;
    const selectedType = eqTypesResponse?.data?.find(
      (t) => t.id === selectedEqTypeId,
    );
    if (!selectedType) return;
    setTypeDialogMode("EDIT");
    setTypeName(selectedType.name);
    setMinDeliveryPhotos(selectedType.minDeliveryPhotos || 0);
    setMinReturnPhotos(selectedType.minReturnPhotos || 0);
    setPhotoInstructions(selectedType.photoInstructions || "");
    setIsTypeDialogVisible(true);
  };

  const handleSaveType = () => {
    if (!typeName.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "O nome do tipo de equipamento é obrigatório.",
        life: 3000,
      });
      return;
    }

    if (typeDialogMode === "CREATE") {
      createTypeMutation.mutate(
        {
          name: typeName.trim(),
          minDeliveryPhotos: Number(minDeliveryPhotos) || 0,
          minReturnPhotos: Number(minReturnPhotos) || 0,
          photoInstructions: photoInstructions || "",
        },
        {
          onSuccess: (res: any) => {
            toast.current?.show({
              severity: "success",
              summary: "Sucesso",
              detail: "Tipo de equipamento criado com sucesso.",
              life: 3000,
            });
            setIsTypeDialogVisible(false);
            if (res?.data?.id) {
              setSelectedEqTypeId(res.data.id);
            }
          },
          onError: (err: any) => {
            toast.current?.show({
              severity: "error",
              summary: "Erro",
              detail: err.message || "Erro ao criar tipo de equipamento.",
              life: 3000,
            });
          },
        },
      );
    } else {
      updateTypeMutation.mutate(
        {
          id: selectedEqTypeId,
          name: typeName.trim(),
          minDeliveryPhotos: Number(minDeliveryPhotos) || 0,
          minReturnPhotos: Number(minReturnPhotos) || 0,
          photoInstructions: photoInstructions || "",
        },
        {
          onSuccess: () => {
            toast.current?.show({
              severity: "success",
              summary: "Sucesso",
              detail: "Tipo de equipamento atualizado com sucesso.",
              life: 3000,
            });
            setIsTypeDialogVisible(false);
          },
          onError: (err: any) => {
            toast.current?.show({
              severity: "error",
              summary: "Erro",
              detail: err.message || "Erro ao atualizar tipo de equipamento.",
              life: 3000,
            });
          },
        },
      );
    }
  };

  const handleDeleteType = () => {
    if (!selectedEqTypeId) return;
    const selectedType = eqTypesResponse?.data?.find(
      (t) => t.id === selectedEqTypeId,
    );
    if (!selectedType) return;

    confirmDialog({
      message: `Tem a certeza que deseja eliminar o tipo de equipamento "${selectedType.name}" e todas as suas características?`,
      header: "Confirmação de Eliminação",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: () => {
        deleteTypeMutation.mutate(selectedEqTypeId, {
          onSuccess: () => {
            toast.current?.show({
              severity: "success",
              summary: "Sucesso",
              detail: "Tipo de equipamento eliminado com sucesso.",
              life: 3000,
            });
            setSelectedEqTypeId("");
          },
          onError: (err: any) => {
            toast.current?.show({
              severity: "error",
              summary: "Erro",
              detail: err.message || "Erro ao eliminar tipo de equipamento.",
              life: 3000,
            });
          },
        });
      },
    });
  };

  const handleOpenNew = () => {
    if (!selectedEqTypeId) {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "Selecione primeiro um tipo de equipamento.",
        life: 3000,
      });
      return;
    }
    setAttrDialogMode("CREATE");
    setNewAttr({
      equipmentTypeId: selectedEqTypeId,
      name: "",
      type: "TEXT",
      isRequired: false,
      isUserEditable: false,
      options: "",
    });
    setIsDialogVisible(true);
  };

  const handleOpenEditAttr = (attr: EquipmentAttributeDefinition) => {
    setAttrDialogMode("EDIT");
    setNewAttr({
      id: attr.id,
      equipmentTypeId: attr.equipmentTypeId,
      name: attr.name,
      type: attr.type,
      isRequired: attr.isRequired,
      isUserEditable: attr.isUserEditable,
      options: attr.options || "",
    });
    setIsDialogVisible(true);
  };

  const handleSave = () => {
    if (!newAttr.name?.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "O nome da característica é obrigatório.",
        life: 3000,
      });
      return;
    }

    let payload: EquipmentAttributeDefinition = {
      ...newAttr,
      name: newAttr.name.trim(),
    };

    if (newAttr.type === "SELECT") {
      const normalizedOptions = (newAttr.options || "")
        .split(",")
        .map((opt) => opt.trim())
        .filter(Boolean)
        .join(",");

      if (!normalizedOptions) {
        toast.current?.show({
          severity: "warn",
          summary: "Aviso",
          detail: "Adicione pelo menos uma opção para o campo de seleção.",
          life: 3000,
        });
        return;
      }

      payload = { ...payload, options: normalizedOptions };
    } else {
      payload = { ...payload, options: null };
    }

    if (attrDialogMode === "CREATE") {
      createAttrMutation.mutate(payload, {
        onSuccess: () => {
          toast.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "Atributo adicionado com sucesso.",
            life: 3000,
          });
          setIsDialogVisible(false);
        },
        onError: (err: any) => {
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: err.message,
            life: 3000,
          });
        },
      });
      return;
    }

    updateAttrMutation.mutate(payload, {
      onSuccess: () => {
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Atributo atualizado com sucesso.",
          life: 3000,
        });
        setIsDialogVisible(false);
      },
      onError: (err: any) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: err.message,
          life: 3000,
        });
      },
    });
  };

  if (loadingTypes) return <Spinner />;

  const attrTypes = [
    { value: "TEXT", label: "Texto" },
    { value: "NUMBER", label: "Número" },
    { value: "BOOLEAN", label: "Sim ou Não" },
    { value: "SELECT", label: "Seleção de Opções" },
  ];

  return (
    <div>
      <div className="flex justify-content-between align-items-center mt-4 mb-3 gap-2">
        <div className="flex align-items-center gap-2">
          <div className="field mt-2 mb-0" style={{ width: "20vw" }}>
            <span className="p-float-label">
              <Dropdown
                id="selectEqType"
                options={eqTypesResponse?.data || []}
                optionLabel="name"
                optionValue="id"
                value={selectedEqTypeId}
                onChange={(e) => setSelectedEqTypeId(e.value)}
                className="w-full"
              />
              <label htmlFor="selectEqType">
                Selecionar Tipo de Equipamento
              </label>
            </span>
          </div>
          <Button
            icon="pi pi-plus" severity="secondary"
            className="p-button-outlined"
            tooltip="Adicionar Tipo de Equipamento"
            onClick={handleOpenCreateType}
          />
          <Button
            icon="pi pi-pencil" severity="secondary"
            className="p-button-outlined"
            tooltip="Editar Tipo de Equipamento"
            onClick={handleOpenEditType}
            disabled={!selectedEqTypeId}
          />
          <Button
            icon="pi pi-trash" severity="secondary"
            className="p-button-outlined"
            tooltip="Eliminar Tipo de Equipamento"
            onClick={handleDeleteType}
            disabled={!selectedEqTypeId}
          />
        </div>
        <Button
          label="Adicionar Atributo"
          icon="pi pi-plus"
          onClick={handleOpenNew}
          disabled={!selectedEqTypeId}
        />
      </div>

      {selectedEqTypeId ? (
        loadingAttrs ? (
          <Spinner />
        ) : (
          <DataTable
            value={attrResponse?.data?.data || []}
            emptyMessage="Sem atributos definidos para este tipo de equipamento."
          >
            <Column field="name" header="Nome da Característica" />
            <Column field="type" header="Tipo de Campo" />
            <Column
              field="options"
              header="Opções"
              body={(row: EquipmentAttributeDefinition) =>
                row.type === "SELECT" ? row.options || "-" : "-"
              }
            />
            <Column
              field="isRequired"
              header="Obrigatório"
              body={(row) => (row.isRequired ? "Sim" : "Não")}
            />
            <Column
              field="isUserEditable"
              header="Editável pelo Utilizador"
              body={(row) => (row.isUserEditable ? "Sim" : "Não")}
            />
            <Column
              header="Ações"
              body={(row: EquipmentAttributeDefinition) => (
                <Button
                  icon="pi pi-pencil" severity="secondary"
                  className="p-button-rounded p-button-text"
                  onClick={() => handleOpenEditAttr(row)}
                  tooltip="Editar atributo"
                />
              )}
              style={{ width: "6rem" }}
            />
          </DataTable>
        )
      ) : (
        <p className="text-center mt-5 text-gray-500">
          Selecione um tipo de equipamento acima para ver e configurar as
          características.
        </p>
      )}

      <Toast ref={toast} />
      <ConfirmDialog />

      <Dialog
        header={
          attrDialogMode === "CREATE"
            ? "Adicionar Característica / Atributo"
            : "Editar Característica / Atributo"
        }
        visible={isDialogVisible}
        style={{ width: "30vw" }}
        onHide={() => setIsDialogVisible(false)}
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              onClick={() => setIsDialogVisible(false)}
              className="p-button-text"
            />
            <Button
              label={attrDialogMode === "CREATE" ? "Adicionar" : "Salvar"}
              icon="pi pi-check"
              onClick={handleSave}
              autoFocus
            />
          </div>
        }
      >
        <div className="flex flex-column gap-4 mt-5">
          <div className="field">
            <InputTextComponent
              fieldName="name"
              label="Nome da Característica (ex: RAM, Combustível, Cor)"
              currentObject={newAttr}
              getValue={newAttr.name}
              setValue={setNewAttr}
            />
          </div>

          <div className="field">
            <span className="p-float-label">
              <Dropdown
                id="type"
                options={attrTypes}
                optionLabel="label"
                optionValue="value"
                value={newAttr.type}
                onChange={(e) => setNewAttr({ ...newAttr, type: e.value })}
                className="w-full"
              />
              <label htmlFor="type">Tipo de Campo</label>
            </span>
          </div>

          {newAttr.type === "SELECT" && (
            <div className="field">
              <span className="p-float-label">
                <InputText
                  id="options"
                  value={newAttr.options || ""}
                  onChange={(e) =>
                    setNewAttr({ ...newAttr, options: e.target.value })
                  }
                  className="w-full"
                />
                <label htmlFor="options">Opções do Dropdown</label>
              </span>
              <small className="text-gray-500">
                Separe as opções por vírgula. Ex: Sim,Não,Não Sei
              </small>
            </div>
          )}

          <div className="flex align-items-center gap-2">
            <Checkbox
              id="isRequired"
              onChange={(e) =>
                setNewAttr({ ...newAttr, isRequired: e.checked || false })
              }
              checked={newAttr.isRequired}
            />
            <label htmlFor="isRequired">Obrigatório</label>
          </div>

          <div className="flex align-items-center gap-2">
            <Checkbox
              id="isUserEditable"
              onChange={(e) =>
                setNewAttr({ ...newAttr, isUserEditable: e.checked || false })
              }
              checked={newAttr.isUserEditable}
            />
            <label htmlFor="isUserEditable">Editável pelo Colaborador</label>
          </div>
        </div>
      </Dialog>

      <Dialog
        header={
          typeDialogMode === "CREATE"
            ? "Adicionar Tipo de Equipamento"
            : "Editar Tipo de Equipamento"
        }
        visible={isTypeDialogVisible}
        style={{ width: "30vw" }}
        onHide={() => setIsTypeDialogVisible(false)}
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              onClick={() => setIsTypeDialogVisible(false)}
              className="p-button-text"
            />
            <Button
              label="Guardar"
              icon="pi pi-check"
              onClick={handleSaveType}
              autoFocus
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3 mt-3">
          <div className="flex flex-column gap-1">
            <label htmlFor="typeName" className="font-bold">
              Nome do Tipo
            </label>
            <InputText
              id="typeName"
              value={typeName}
              onChange={(e) => setTypeName(e.target.value)}
              placeholder="Ex: Portátil, Smartphone, Viatura"
              className="w-full"
              autoFocus
            />
          </div>

          <div className="flex gap-3">
            <div className="flex flex-column gap-1 w-6">
              <label htmlFor="minDeliveryPhotos" className="font-bold">
                Mín. Fotos Entrega
              </label>
              <InputText
                id="minDeliveryPhotos"
                type="number"
                value={String(minDeliveryPhotos)}
                onChange={(e) =>
                  setMinDeliveryPhotos(Number(e.target.value) || 0)
                }
                className="w-full"
              />
            </div>
            <div className="flex flex-column gap-1 w-6">
              <label htmlFor="minReturnPhotos" className="font-bold">
                Mín. Fotos Devolução
              </label>
              <InputText
                id="minReturnPhotos"
                type="number"
                value={String(minReturnPhotos)}
                onChange={(e) =>
                  setMinReturnPhotos(Number(e.target.value) || 0)
                }
                className="w-full"
              />
            </div>
          </div>

          <div className="flex flex-column gap-1">
            <label htmlFor="photoInstructions" className="font-bold">
              Instruções das Fotos (Opcional)
            </label>
            <textarea
              id="photoInstructions"
              rows={4}
              value={photoInstructions}
              onChange={(e) => setPhotoInstructions(e.target.value)}
              placeholder="Ex: Tirar foto de frente, traseira, lados e interior da viatura..."
              className="w-full p-inputtext"
              style={{ resize: "vertical" }}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default EquipmentTemplatesTab;
